/*
 * printNodeOrderItemColumns.ts
 *
 * Which columns each role sees on the Order Items screen.
 *
 * WHY THIS IS A SEPARATE FILE
 * ---------------------------
 * The policy used to live inline in `PrintNodeOrderItemsComponent.displayedColumns`,
 * expressed as "organization hides three columns, serviceProvider gets the
 * no-production set, EVERYTHING ELSE falls through to the full set". That is
 * correct but it is not legible: no reader can answer "does the facility role
 * see Cumulative Pages?" without reasoning about which branch `facility` fails
 * to match. It was asked, and the answer was not obvious from the code.
 *
 * Pulling it out means the policy can be stated once, named per role, and
 * pinned by `tests/PrintNodeOrderItemColumns.smoke.ts` under plain ts-node
 * without dragging in Angular.
 */

/**
 * Columns suppressed for the `organization` role.
 *
 * These are internal production/operational figures. An organization is
 * looking at its own orders and has no use for our facility name, our sheet
 * counts, or a running page total across a manufacturing queue it cannot see.
 */
export const COLUMNS_HIDDEN_FOR_ORGANIZATION = ['orgName', 'sheets', 'cumulativeNormalizedPages'];

/**
 * Roles that see the full production column set - including Cumulative Pages.
 *
 * `facility` and `network` are both manufacturing-facing: they are planning and
 * executing print work, which is exactly what the cumulative page running total
 * is for. Listed explicitly rather than left to a default branch so that adding
 * a new role is a deliberate decision instead of an accident.
 */
export const ROLES_WITH_PRODUCTION_COLUMNS = ['facility', 'network'];

/**
 * Decide the column list for a role.
 *
 * @param role                the value of `StartupService.selectedRole`
 * @param withProduction      the full manufacturing column set
 * @param withoutProduction   the set with production data suppressed
 */
export function columnsForRole<T extends { columnDef?: string }>(
  role: string,
  withProduction: T[],
  withoutProduction: T[]
): T[] {
  if (role === 'organization') {
    return withoutProduction.filter(
      c => c && COLUMNS_HIDDEN_FOR_ORGANIZATION.indexOf(c.columnDef) === -1);
  }
  if (role === 'serviceProvider') {
    return withoutProduction;
  }
  // facility, network, and anything not yet named. The default stays permissive
  // deliberately: a manufacturing operator seeing one column too many is a much
  // smaller problem than an unrecognised role rendering an empty table.
  return withProduction;
}
